import React, { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Check, XCircle, Loader } from "lucide-react";

const MAX_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function BillingSuccess() {
  const [params] = useSearchParams();
  // Razorpay returns order_id; older Stripe links used session_id — support both
  const orderId = params.get("order_id") || params.get("session_id");
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "checking", message: "Confirming your payment…" });
  const attemptsRef = useRef(0);

  useEffect(() => {
    if (!orderId) {
      setState({ status: "error", message: "No order id in URL." });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${orderId}`);
        if (data.payment_status === "paid") {
          await refresh();
          setState({ status: "paid", message: "Payment confirmed. Premium unlocked." });
          return;
        }
        if (data.status === "signature_failed" || data.status === "expired") {
          setState({ status: "expired", message: "Payment could not be verified." });
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setState({ status: "timeout", message: "Still confirming. Check again in a moment or contact support." });
          return;
        }
        setState({ status: "checking", message: "Payment is being processed…" });
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (e) {
        setState({ status: "error", message: e.response?.data?.detail || "Failed to verify payment." });
      }
    };

    poll();
    return () => { cancelled = true; };
  }, [orderId, refresh]);

  return (
    <div className="min-h-[70vh] flex items-center justify-center" data-testid="billing-success-page">
      <div className="w-full max-w-md text-center bg-white border-2 border-black p-10">
        {state.status === "checking" && (
          <>
            <Loader size={40} className="mx-auto animate-spin" />
            <h2 className="font-display font-black text-3xl tracking-tighter mt-4">Verifying…</h2>
          </>
        )}
        {state.status === "paid" && (
          <>
            <div className="w-16 h-16 bg-[var(--success-green)] flex items-center justify-center mx-auto">
              <Check size={32} className="text-white" />
            </div>
            <h2 className="font-display font-black text-3xl tracking-tighter mt-4">Premium Unlocked.</h2>
            <p className="font-mono text-sm text-[var(--text-secondary)] mt-3">Welcome to RED.FLAG Premium. The Coach is ready.</p>
            <button
              data-testid="go-to-coach"
              onClick={() => navigate("/coach")}
              className="mt-6 w-full bg-black text-white py-3 font-display font-bold tracking-wider hover:bg-[var(--alert-red)] transition-colors"
            >
              OPEN COACH →
            </button>
          </>
        )}
        {(state.status === "error" || state.status === "expired" || state.status === "timeout") && (
          <>
            <XCircle size={40} className="mx-auto text-[var(--alert-red)]" />
            <h2 className="font-display font-black text-3xl tracking-tighter mt-4">Hmm.</h2>
            <p className="font-mono text-sm text-[var(--text-secondary)] mt-3">{state.message}</p>
            <Link
              to="/upgrade"
              data-testid="retry-checkout"
              className="mt-6 inline-block w-full bg-black text-white py-3 font-display font-bold tracking-wider hover:bg-[var(--action-blue)] transition-colors"
            >
              TRY AGAIN →
            </Link>
          </>
        )}
        <div data-testid="billing-status" className="overline mt-6">{state.message}</div>
      </div>
    </div>
  );
}
